import React, { useState, useEffect, useMemo } from 'react';
import { terminologyAvailabilityFromError } from 'src/util/terminologyAvailability';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  OutlinedInput
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'src/axios';
import { HPO_CONSTANTS, PHENOTYPES_CONSTANTS } from 'src/components/Constants';
import { getHPOSearchURL, getPhenotypeSearchURL } from 'src/util/util';
import {
  AddressForm,
  AppointmentForm,
  ClinicalNotesForm,
  ConsentForm,
  ContactForm,
  ReferralForm,
  SampleForm,
  TestForm,
  TreatmentForm,
  TissuePathologyForm
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/FormsExport';

// ── Shared search autocomplete ────────────────────────────────────────────────

function RecordSearch({
  label,
  fetchOptions,
  onAdd,
  placeholder = 'Search...'
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(null);
  const [age, setAge] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const showAge = label === 'Disease';

  const handleInputChange = async (_, value, reason) => {
    setSearchTerm(value);
    if (reason !== 'input') return;
    if (value.length < 3) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const results = await fetchOptions(value);
      setOptions(results);
      setUnavailable(null);
    } catch (err) {
      // try/finally with no catch: a rejection escaped as an unhandled
      // promise rejection, setOptions never ran, and the PREVIOUS term's
      // options stayed on screen under the new term — offering BRCA1/BRCA2
      // while "MLH1" was in the box (TGCLOUD-1785).
      setUnavailable(terminologyAvailabilityFromError());
      setOptions([]);
      console.error('Clinical record lookup failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!selected) return;
    onAdd(showAge ? { ...selected, age: age || undefined } : selected);
    setSelected(null);
    setSearchTerm('');
    setOptions([]);
    setAge('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap'
      }}
    >
      <Autocomplete
        size="small"
        sx={{ flex: '1 1 200px', minWidth: 160 }}
        options={options}
        value={selected}
        inputValue={searchTerm}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        filterOptions={(x) => x}
        // This field attaches a record to several family members at once, so
        // an empty list that actually means "the lookup failed" is the worst
        // possible thing to leave unlabelled.
        noOptionsText={
          unavailable
            ? 'Lookup unavailable — this is not a result'
            : searchTerm.length < 3
            ? 'Type at least 3 characters'
            : 'No matches'
        }
        loading={loading}
        open={popupOpen}
        onOpen={() => setPopupOpen(true)}
        onClose={() => setPopupOpen(false)}
        onChange={(_, val) => setSelected(val)}
        onInputChange={handleInputChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`Search for ${label}s`}
            placeholder={placeholder}
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !popupOpen && selected) {
                e.preventDefault();
                handleAdd();
              }
            }}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                sx: { height: 32, fontSize: 13 },
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={14} />}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              },

              inputLabel: { sx: { fontSize: 13 } }
            }}
          />
        )}
      />
      {showAge && (
        <TextField
          size="small"
          label="Age onset"
          value={age}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '' || /^\d{0,3}$/.test(v)) setAge(v);
          }}
          sx={{ width: 90 }}
          slotProps={{
            input: { sx: { height: 32, fontSize: 13 } },
            inputLabel: { sx: { fontSize: 13 } }
          }}
        />
      )}
      <IconButton
        size="small"
        color="primary"
        onClick={handleAdd}
        disabled={!selected}
        title={`Add ${label}`}
        sx={{ width: 32, height: 32 }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// ── Section header ─────────────────────────────────────────────────────────

function SectionHeader({ label }) {
  return (
    <Typography
      variant="overline"
      sx={{
        fontWeight: 700,
        color: 'text.secondary',
        lineHeight: 1.2,
        display: 'block',
        mb: 0.5
      }}
    >
      {label}
    </Typography>
  );
}

// ── Record chip list ───────────────────────────────────────────────────────

function RecordChip({ label, onDelete, strikethrough, unsaved }) {
  return (
    <Chip
      size="small"
      label={label}
      onDelete={onDelete}
      deleteIcon={
        <CloseIcon
          style={{ fontSize: 14, color: unsaved ? '#BA7517' : undefined }}
        />
      }
      sx={{
        height: 24,
        fontSize: 12,
        textDecoration: strikethrough ? 'line-through' : 'none',
        opacity: strikethrough ? 0.5 : 1,
        ...(unsaved && {
          bgcolor: '#FFF3E0',
          color: '#BA7517',
          border: '1px solid #BA7517',
          '& .MuiChip-deleteIcon': { color: '#BA7517' }
        })
      }}
    />
  );
}

// ── Fetch helpers ──────────────────────────────────────────────────────────

async function fetchDiseaseOptions(term) {
  const url = getHPOSearchURL(
    term,
    HPO_CONSTANTS.initialPage,
    HPO_CONSTANTS.category.disease
  );
  const { data } = await axios.get(url);
  return (data.results || []).map((d) => ({ id: d.id, name: d.name }));
}

async function fetchPhenotypeOptions(term) {
  const url = getPhenotypeSearchURL(term, PHENOTYPES_CONSTANTS.initialPage);
  const { data } = await axios.get(url);
  return (data.terms || []).map((d) => ({
    id: d.id,
    name: d.name,
    definition: d.definition,
    comment: d.comment,
    synonyms: d.synonyms
  }));
}

async function fetchGenotypeOptions(term) {
  const url = getHPOSearchURL(
    term,
    HPO_CONSTANTS.initialPage,
    HPO_CONSTANTS.category.gene
  );
  const { data } = await axios.get(url);
  return (data.results || []).map((d) => ({ id: d.id, name: d.name }));
}

// ── Clinical record type options ───────────────────────────────────────────

const CLINICAL_NOTE_TYPES = [
  'Address',
  'Appointment',
  'Clinical Note',
  'Consent',
  'Contact',
  'Referral',
  'Sample',
  'Test',
  'Treatment',
  'Tissue Pathology'
];

// ── Clinical records create section ───────────────────────────────────────

function ClinicalRecordsCreate({ member, familyId }) {
  const [selectedType, setSelectedType] = useState('');
  const [open, setOpen] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [unsavedChanges, setUnsavedChanges] = useState({
    edit: false,
    status: false
  });

  const staffMemberOptions = useMemo(
    () =>
      staffMembers.map((d) => ({
        label: `${d.firstName} ${d.lastName}`,
        value: d.userId
      })),
    [staffMembers]
  );

  useEffect(() => {
    axios.get('/clinical-notes/staff-members').then((res) => {
      setStaffMembers(
        res.data.map((m) => ({
          firstName: m.firstName,
          lastName: m.lastName,
          userId: m._id
        }))
      );
    });
  }, []);

  const familyMemberId = member.proBandId ?? member.familyMemberId;

  const handleSelect = (e) => {
    if (e.target.value) {
      setSelectedType(e.target.value);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedType('');
    setUnsavedChanges({ edit: false, status: false });
  };

  const formProps = {
    handleClose,
    familyMemberId,
    familyId,
    isEditing: false,
    model: {},
    staffMembers,
    staffMemberOptions,
    isDisabled: false,
    setUnsavedChanges,
    data: member
  };

  const renderForm = () => {
    switch (selectedType) {
      case 'Address':
        return <AddressForm {...formProps} />;
      case 'Appointment':
        return <AppointmentForm {...formProps} />;
      case 'Clinical Note':
        return <ClinicalNotesForm {...formProps} />;
      case 'Consent':
        return <ConsentForm {...formProps} />;
      case 'Contact':
        return <ContactForm {...formProps} />;
      case 'Referral':
        return <ReferralForm {...formProps} />;
      case 'Sample':
        return <SampleForm {...formProps} />;
      case 'Test':
        return <TestForm {...formProps} refreshPedigreeLabels={() => {}} />;
      case 'Treatment':
        return <TreatmentForm {...formProps} />;
      case 'Tissue Pathology':
        return <TissuePathologyForm {...formProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <SectionHeader label="CLINICAL RECORDS" />
      <Select
        value=""
        onChange={handleSelect}
        input={<OutlinedInput />}
        displayEmpty
        size="small"
        sx={{ width: '100%', fontSize: 13, height: 32 }}
        renderValue={() => (
          <span style={{ color: '#999', fontSize: 13 }}>
            Create New Clinical Record
          </span>
        )}
      >
        {CLINICAL_NOTE_TYPES.map((t) => (
          <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
            {t}
          </MenuItem>
        ))}
      </Select>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            bgcolor: 'grey.900',
            color: 'white'
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600
            }}
          >
            Create New {selectedType}
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ color: 'white' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, pt: '24px !important' }}>
          {renderForm()}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────

export default function ClinicalRecordsPanel({
  member,
  entry,
  actions,
  familyId
}) {
  const existingDiseases = (member.diagnoses || []).filter(
    (d) => !entry.removedDiseaseIds.includes(d._id)
  );
  const existingPhenotypes = (member.phenotypes || []).filter(
    (p) => !entry.removedPhenotypeIds.includes(p.id)
  );
  const existingGenotypes = (member.genotypes || []).filter(
    (g) => !entry.removedGenotypeIds.includes(g.id)
  );

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: 'grey.50',
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
        gap: 3
      }}
    >
      {/* ── Diseases ─────────────────────────────── */}
      <Box>
        <SectionHeader label="DISEASES" />
        <RecordSearch
          label="Disease"
          fetchOptions={fetchDiseaseOptions}
          onAdd={(disease) => actions.addDisease(member.pid, disease)}
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 1
          }}
        >
          {existingDiseases.length === 0 && entry.newDiseases.length === 0 && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary'
              }}
            >
              None recorded
            </Typography>
          )}
          {existingDiseases.map((d) => (
            <RecordChip
              key={d._id}
              label={d.age ? `${d.name} (${d.age})` : d.name}
              onDelete={() => actions.markDiseaseRemoved(member.pid, d._id)}
            />
          ))}
          {entry.newDiseases.map((d) => (
            <RecordChip
              key={d.id}
              label={d.age ? `${d.name} (${d.age})` : d.name}
              onDelete={() => actions.removeNewDisease(member.pid, d.id)}
              unsaved
            />
          ))}
        </Box>
      </Box>
      {/* ── Phenotypes ───────────────────────────── */}
      <Box>
        <SectionHeader label="PHENOTYPES" />
        <RecordSearch
          label="Phenotype"
          fetchOptions={fetchPhenotypeOptions}
          onAdd={(phenotype) => actions.addPhenotype(member.pid, phenotype)}
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 1
          }}
        >
          {existingPhenotypes.length === 0 &&
            entry.newPhenotypes.length === 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary'
                }}
              >
                None recorded
              </Typography>
            )}
          {existingPhenotypes.map((p) => (
            <RecordChip
              key={p.id}
              label={p.name}
              onDelete={() => actions.markPhenotypeRemoved(member.pid, p.id)}
            />
          ))}
          {entry.newPhenotypes.map((p) => (
            <RecordChip
              key={p.id}
              label={p.name}
              onDelete={() => actions.removeNewPhenotype(member.pid, p.id)}
              unsaved
            />
          ))}
        </Box>
      </Box>
      {/* ── Genotypes ────────────────────────────── */}
      <Box>
        <SectionHeader label="GENOTYPES" />
        <RecordSearch
          label="Gene"
          fetchOptions={fetchGenotypeOptions}
          onAdd={(gene) => actions.addGenotype(member.pid, gene)}
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            mt: 1
          }}
        >
          {existingGenotypes.length === 0 &&
            entry.newGenotypes.length === 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary'
                }}
              >
                None recorded
              </Typography>
            )}
          {existingGenotypes.map((g) => (
            <RecordChip
              key={g.id}
              label={g.name || g.symbol || g.id}
              onDelete={() => actions.markGenotypeRemoved(member.pid, g.id)}
            />
          ))}
          {entry.newGenotypes.map((g) => (
            <RecordChip
              key={g.id}
              label={g.name}
              onDelete={() => actions.removeNewGenotype(member.pid, g.id)}
              unsaved
            />
          ))}
        </Box>
      </Box>
      {/* ── Clinical Records ─────────────────────── */}
      <Box>
        <ClinicalRecordsCreate member={member} familyId={familyId} />
      </Box>
    </Box>
  );
}
